import { useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, SyntheticEvent } from 'react'
import { useMonitoredInboxes } from '@/hooks/useMonitoredInboxes'
import {
  completeInboxAtCursor,
  completeInboxFilterTokens,
  getInboxInlineSuggestion,
} from '@/utils/inboxFilter'

interface InboxFilterInputProps {
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
}

const GHOST_LAYER_CLASS = 'inbox-filter-input-ghost'

export default function InboxFilterInput({
  value,
  onChange,
  className = 'input-sm',
  placeholder,
}: InboxFilterInputProps) {
  const configured = useMonitoredInboxes()
  const inputRef = useRef<HTMLInputElement>(null)
  const [cursorPos, setCursorPos] = useState(value.length)

  const suggestion =
    configured.length > 0
      ? getInboxInlineSuggestion(value, Math.min(cursorPos, value.length), configured)
      : null

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) return
    setCursorPos(input.selectionStart ?? value.length)
  }, [value])

  function syncCursor(event?: SyntheticEvent<HTMLInputElement>) {
    const input = event?.currentTarget ?? inputRef.current
    if (!input) return
    setCursorPos(input.selectionStart ?? value.length)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Tab' || e.shiftKey || configured.length === 0) return

    const input = inputRef.current
    if (!input) return

    const selectionStart = input.selectionStart ?? value.length
    const completed = completeInboxAtCursor(value, selectionStart, configured)
    if (!completed) return
    if (completed.value === value && completed.cursorPos === selectionStart) return

    e.preventDefault()
    onChange(completed.value)
    requestAnimationFrame(() => {
      input.setSelectionRange(completed.cursorPos, completed.cursorPos)
      setCursorPos(completed.cursorPos)
    })
  }

  function handleBlur() {
    if (configured.length === 0) return
    const normalized = completeInboxFilterTokens(value, configured)
    if (normalized !== value) onChange(normalized)
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setCursorPos(e.target.selectionStart ?? e.target.value.length)
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={syncCursor}
        onClick={syncCursor}
        onSelect={syncCursor}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
      {suggestion && (
        <div aria-hidden className={GHOST_LAYER_CLASS}>
          <span className="inbox-filter-input-ghost-prefix select-none">
            {suggestion.prefixBeforeGhost}
          </span>
          <span className="inbox-filter-input-ghost-suffix select-none">
            {suggestion.ghostSuffix}
          </span>
        </div>
      )}
    </div>
  )
}
